import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { shareLinksApi } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  File, FilePdf, FileImage, FileText, FileDoc, Folder, Download, Eye, Warning, Clock, CaretRight, House, LinkSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const FILE_ICONS = {
  'application/pdf': FilePdf,
  'image/': FileImage,
  'text/': FileText,
  'application/msword': FileDoc,
  'application/vnd.openxmlformats': FileDoc,
};

function getFileIcon(contentType) {
  for (const [key, Icon] of Object.entries(FILE_ICONS)) {
    if (contentType?.includes(key)) return Icon;
  }
  return File;
}

function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function isPreviewable(ct) {
  return ct && (ct.startsWith('image/') || ct === 'application/pdf' || ct.startsWith('text/') || ct.startsWith('video/') || ct.startsWith('audio/'));
}

export default function SharedItemView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null); // for folder share navigation
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await shareLinksApi.view(token);
      setData(res.data);
      if (res.data.type === 'folder') {
        setCurrentFolderId(res.data.folder.id);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Share link unavailable');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const download = (docId, filename) => {
    const url = shareLinksApi.downloadUrl(token, docId);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'file';
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-8 text-center" data-testid="share-error">
          <Warning size={48} className="mx-auto text-destructive mb-4" />
          <h1 className="font-heading text-xl font-bold mb-2">Link Unavailable</h1>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const brand = data?.brand || {};
  const allowDownload = !!data?.allow_download;

  return (
    <div className="min-h-screen bg-background" data-testid="share-view">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-5xl mx-auto p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {brand.logo_url && (
              <img src={brand.logo_url.startsWith('http') ? brand.logo_url : `${process.env.REACT_APP_BACKEND_URL}/api/documents/${brand.logo_url}/download`} alt="" className="h-8 w-8 object-contain rounded-sm" />
            )}
            <div>
              <h1 className="font-heading text-lg font-bold">{brand.app_name || 'Shared'}</h1>
              <p className="text-xs text-muted-foreground flex items-center gap-1"><LinkSimple size={10} /> Shared link</p>
            </div>
          </div>
          {data?.expires_at && (
            <Badge variant="outline" className="text-xs"><Clock size={12} className="mr-1" /> Expires {formatDate(data.expires_at)}</Badge>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        {data?.type === 'document' ? (
          <DocumentShareView doc={data.document} allowDownload={allowDownload} token={token} onDownload={() => download(data.document.id, data.document.original_filename)} />
        ) : (
          <FolderShareView
            data={data}
            currentFolderId={currentFolderId}
            setCurrentFolderId={setCurrentFolderId}
            allowDownload={allowDownload}
            onDownload={(id, fn) => download(id, fn)}
            onPreview={(doc) => setPreviewDoc(doc)}
          />
        )}
      </main>

      {previewDoc && (
        <InlinePreviewModal
          doc={previewDoc}
          url={shareLinksApi.previewUrl(token, previewDoc.id)}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => download(previewDoc.id, previewDoc.original_filename)}
          allowDownload={allowDownload}
        />
      )}
    </div>
  );
}

function DocumentShareView({ doc, allowDownload, token, onDownload }) {
  const Icon = getFileIcon(doc.content_type);
  const canPreview = isPreviewable(doc.content_type);
  const previewUrl = shareLinksApi.previewUrl(token, doc.id);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-6 flex items-start gap-4">
        <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon size={28} className="text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-2xl font-bold break-words">{doc.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{doc.original_filename} · {formatFileSize(doc.size)}</p>
          {doc.description && <p className="text-sm text-muted-foreground mt-2">{doc.description}</p>}
        </div>
        {allowDownload && (
          <Button onClick={onDownload} data-testid="share-doc-download">
            <Download size={16} className="mr-2" /> Download
          </Button>
        )}
      </div>

      {/* Inline preview */}
      {canPreview && (
        <div className="bg-card border border-border rounded-xl overflow-hidden" style={{ height: '75vh' }} data-testid="share-doc-preview">
          {doc.content_type?.startsWith('image/') && (
            <div className="h-full w-full flex items-center justify-center bg-muted/30 p-4">
              <img src={previewUrl} alt={doc.name} className="max-h-full max-w-full object-contain" />
            </div>
          )}
          {doc.content_type === 'application/pdf' && (
            <iframe src={previewUrl} title={doc.name} className="w-full h-full border-0" />
          )}
          {doc.content_type?.startsWith('text/') && (
            <iframe src={previewUrl} title={doc.name} className="w-full h-full border-0 bg-white" />
          )}
          {doc.content_type?.startsWith('video/') && (
            <div className="h-full w-full flex items-center justify-center bg-black p-4">
              <video src={previewUrl} controls className="max-h-full max-w-full" />
            </div>
          )}
          {doc.content_type?.startsWith('audio/') && (
            <div className="h-full w-full flex items-center justify-center p-8">
              <audio src={previewUrl} controls className="w-full max-w-md" />
            </div>
          )}
        </div>
      )}
      {!canPreview && (
        <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
          <File size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
          <p className="text-sm text-muted-foreground">Preview not available for this file type.</p>
          {allowDownload && <Button className="mt-4" onClick={onDownload}><Download size={14} className="mr-2" /> Download to view</Button>}
        </div>
      )}
    </div>
  );
}

function FolderShareView({ data, currentFolderId, setCurrentFolderId, allowDownload, onDownload, onPreview }) {
  // Build breadcrumb up from currentFolderId to the root-shared folder
  const allFolders = [data.folder, ...(data.subfolders || [])];
  const folderMap = Object.fromEntries(allFolders.map(f => [f.id, f]));
  const rootId = data.folder.id;

  const computeBreadcrumb = () => {
    const crumbs = [];
    let cur = folderMap[currentFolderId];
    while (cur) {
      crumbs.unshift(cur);
      if (cur.id === rootId) break;
      cur = folderMap[cur.parent_id];
    }
    return crumbs;
  };
  const breadcrumb = computeBreadcrumb();

  // Filter subfolders directly under currentFolderId
  const childFolders = (data.subfolders || []).filter(f => f.parent_id === currentFolderId);
  const folderDocs = (data.documents || []).filter(d => d.folder_id === currentFolderId);

  return (
    <div className="space-y-4">
      {/* Folder Header */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Folder size={24} className="text-primary" weight="fill" />
          </div>
          <div>
            <h2 className="font-heading text-2xl font-bold">{data.folder.name}</h2>
            <p className="text-xs text-muted-foreground">
              {data.subfolders?.length || 0} folders · {data.documents?.length || 0} documents {allowDownload ? '· Download enabled' : '· View only'}
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm flex-wrap">
        <button onClick={() => setCurrentFolderId(rootId)} className="flex items-center gap-1 px-2 py-1 rounded-md hover:bg-muted text-muted-foreground">
          <House size={14} /> {data.folder.name}
        </button>
        {breadcrumb.slice(1).map((f, i) => (
          <React.Fragment key={f.id}>
            <CaretRight size={12} className="text-muted-foreground" />
            <button onClick={() => setCurrentFolderId(f.id)} className={`px-2 py-1 rounded-md hover:bg-muted ${f.id === currentFolderId ? 'font-semibold' : 'text-muted-foreground'}`}>
              {f.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Subfolders */}
      {childFolders.length > 0 && (
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Folders</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {childFolders.map(f => (
              <button
                key={f.id}
                onClick={() => setCurrentFolderId(f.id)}
                className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all text-left"
                data-testid={`share-folder-${f.id}`}
              >
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Folder size={20} className="text-primary" weight="fill" />
                </div>
                <p className="font-medium truncate">{f.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-3 border-b border-border"><p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Documents</p></div>
        {folderDocs.length === 0 ? (
          <p className="text-center text-muted-foreground text-sm p-8">No documents in this folder</p>
        ) : (
          <div className="divide-y divide-border">
            {folderDocs.map(doc => {
              const Icon = getFileIcon(doc.content_type);
              const canPreview = isPreviewable(doc.content_type);
              return (
                <div key={doc.id} className="p-4 flex items-center justify-between gap-4 hover:bg-muted/30" data-testid={`share-doc-${doc.id}`}>
                  <button
                    onClick={() => canPreview ? onPreview(doc) : allowDownload && onDownload(doc.id, doc.original_filename)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left hover:text-primary transition-colors"
                  >
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon size={18} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{doc.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{doc.original_filename} · {formatFileSize(doc.size)}</p>
                    </div>
                  </button>
                  <div className="flex gap-1">
                    {canPreview && (
                      <Button variant="outline" size="sm" onClick={() => onPreview(doc)} data-testid={`share-preview-${doc.id}`}>
                        <Eye size={14} className="mr-1" /> Preview
                      </Button>
                    )}
                    {allowDownload && (
                      <Button variant="outline" size="sm" onClick={() => onDownload(doc.id, doc.original_filename)} data-testid={`share-dl-${doc.id}`}>
                        <Download size={14} className="mr-1" /> Download
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function InlinePreviewModal({ doc, url, onClose, onDownload, allowDownload }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="preview-modal">
      <div className="bg-card border border-border rounded-xl w-full max-w-5xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-border flex items-center justify-between gap-4">
          <p className="font-medium truncate">{doc.name}</p>
          <div className="flex items-center gap-2">
            {allowDownload && (
              <Button variant="outline" size="sm" onClick={onDownload}>
                <Download size={14} className="mr-1" /> Download
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-muted/30">
          {doc.content_type?.startsWith('image/') && (
            <div className="h-full w-full flex items-center justify-center p-4">
              <img src={url} alt={doc.name} className="max-h-full max-w-full object-contain" />
            </div>
          )}
          {doc.content_type === 'application/pdf' && <iframe src={url} title={doc.name} className="w-full h-full border-0" />}
          {doc.content_type?.startsWith('text/') && <iframe src={url} title={doc.name} className="w-full h-full border-0 bg-white" />}
          {doc.content_type?.startsWith('video/') && (
            <div className="h-full w-full flex items-center justify-center bg-black p-4">
              <video src={url} controls className="max-h-full max-w-full" />
            </div>
          )}
          {doc.content_type?.startsWith('audio/') && (
            <div className="h-full w-full flex items-center justify-center p-8">
              <audio src={url} controls className="w-full max-w-md" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
